import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { subMonths, format } from 'date-fns';
import Select from 'react-select';
import DateRangeSelector from '../components/DateRangeSelector';
import { useSelectStyles } from '../utils/selectStyles';
import TelematicSummary from '../components/Telematic/TelematicSummary';
import TelematicDrivers from '../components/Telematic/TelematicDrivers';
import TelematicTable from '../components/Telematic/TelematicTable';
import useTabParam from '../hooks/useTabParam.js';

function TelematicViewer({ fetchWithAuth }) {
  // State pour les données et sélections
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [startDate, setStartDate] = useState(subMonths(new Date(), 3));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [telematicData, setTelematicData] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [activeTab, setActiveTab] = useTabParam(['summary', 'drivers', 'table'], 'summary');

  // Utiliser le hook de styles mutualisés
  const selectStyles = useSelectStyles();

  // Récupération des divisions
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        setLoading(true);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/telematic/divisions?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
        );

        if (response.ok) {
          const divisionsData = await response.json();
          const formattedDivisions = divisionsData.map(div => ({
            value: div.DIVISION,
            label: div.DIVISION
          }));
          setDivisions(formattedDivisions);
        } else {
          console.error("Failed to fetch divisions");
        }
      } catch (error) {
        console.error("Error fetching divisions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDivisions();
  }, [fetchWithAuth, startDate, endDate]);

  // Récupération des données télématiques
  useEffect(() => {
    const fetchTelematicData = async () => {
      if (!selectedDivision) return;

      try {
        setLoading(true);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/telematic/divisions/${selectedDivision.value}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
        );

        if (response.ok) {
          const data = await response.json();
          setTelematicData(data);

          // Extraire la liste des conducteurs
          const uniqueDrivers = [...new Set(data.map(item => item.DRIVER_NAME))];
          const driverOptions = uniqueDrivers.map(name => ({
            value: name,
            label: name
          }));
          setDrivers(driverOptions);
        } else {
          console.error("Failed to fetch telematic data");
        }
      } catch (error) {
        console.error("Error fetching telematic data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTelematicData();
  }, [selectedDivision, startDate, endDate, fetchWithAuth]);

  // Filtrer les données par conducteur sélectionné
  const filteredData = useMemo(() => {
    if (!selectedDriver) return telematicData;
    return telematicData.filter(item => item.DRIVER_NAME === selectedDriver.value);
  }, [telematicData, selectedDriver]);

  // Handler pour le changement de division
  const handleDivisionChange = (newDivision) => {
    setSelectedDivision(newDivision);
    setSelectedDriver(null);
  };

  // Handler pour le changement de plage de dates
  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  return (
    <div className="container mx-auto p-4 relative">
      <h1 className="text-3xl font-bold mb-6 dark:text-white">
        <span className="hidden sm:inline">Visualisation des Données Télématiques</span>
        <span className="sm:hidden">Télématique</span>
      </h1>

      {/* Header de sélection */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6 relative z-20">
        <div className="flex flex-col lg:flex-row items-end gap-3">
          {/* Sélection de division */}
          <div className="w-full lg:w-1/3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
              Division
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={divisions}
              value={selectedDivision}
              onChange={handleDivisionChange}
              isLoading={loading}
              placeholder="Sélectionner une division..."
              isDisabled={loading}
              menuPortalTarget={document.body}
              isClearable={true}
              styles={selectStyles}
            />
          </div>

          {/* Sélection de conducteur */}
          <div className="w-full lg:w-1/3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
              Conducteur
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={drivers}
              value={selectedDriver}
              onChange={setSelectedDriver}
              isLoading={loading}
              placeholder="Tous les conducteurs"
              isDisabled={loading || !selectedDivision}
              menuPortalTarget={document.body}
              isClearable={true}
              styles={selectStyles}
            />
          </div>

          {/* Sélection de dates */}
          <div className="w-full lg:w-1/3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
              Période
            </label>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onDatesChange={handleDateRangeChange}
              maxDateRange={{ value: 6, unit: 'months' }}
              relative={false}
            />
          </div>
        </div>
      </div>

      {/* Indicateur de chargement */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Tabs de navigation */}
      {selectedDivision && !loading && (
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex flex-wrap space-x-4 sm:space-x-6">
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'summary'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('summary')}
              >
                Résumé
              </button>
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'drivers'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('drivers')}
              >
                Conducteurs
              </button>
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'table'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('table')}
              >
                Détails
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      {selectedDivision && !loading && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          {activeTab === 'summary' && (
            <TelematicSummary
              data={filteredData}
              driverName={selectedDriver?.label || 'Tous les conducteurs'}
              dateRange={`${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`}
            />
          )}

          {activeTab === 'drivers' && (
            <TelematicDrivers
              data={filteredData}
              dateRange={`${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`}
            />
          )}

          {activeTab === 'table' && (
            <TelematicTable
              data={filteredData}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </div>
      )}

      {/* État vide */}
      {!selectedDivision && !loading && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <p className="text-gray-600 dark:text-gray-400 text-center">
            Sélectionnez une division pour visualiser les données télématiques.
          </p>
        </div>
      )}
    </div>
  );
}

export default TelematicViewer;